import { useMemo } from 'react';
import Select, { type SelectOption } from './Select';
import { COUNTRIES } from '../../lib/countries';

export function CodeChip({ code }: { code: string }) {
  return (
    <span className="inline-flex h-5 min-w-[26px] shrink-0 items-center justify-center rounded-md bg-[#fdf2f8] px-1 text-[10px] font-bold tracking-wide text-[#d6006e]" style={{ border: '1px solid #f6dbe7' }}>
      {code}
    </span>
  );
}

type Props = {
  value: string;
  onChange: (code: string, name: string) => void;
  /** Countries shown first with a note, e.g. already configured ones. */
  highlight?: Record<string, string>;
  /** Hide these codes entirely. */
  exclude?: string[];
  extraOptions?: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
};

export default function CountrySelect({ value, onChange, highlight = {}, exclude = [], extraOptions = [], placeholder = 'Choose a country', disabled, id }: Props) {
  const options = useMemo<SelectOption[]>(() => {
    const skip = new Set(exclude);
    const rows = COUNTRIES.filter((c) => !skip.has(c.code)).map((c) => ({
      value: c.code,
      label: c.name,
      description: highlight[c.code],
      prefix: <CodeChip code={c.code} />,
      keywords: c.code,
    }));
    const first = rows.filter((r) => highlight[r.value]);
    const rest = rows.filter((r) => !highlight[r.value]);
    return [...extraOptions, ...first, ...rest];
  }, [highlight, exclude, extraOptions]);

  const names = useMemo(() => new Map(options.map((o) => [o.value, o.label])), [options]);

  return (
    <Select
      id={id}
      value={value}
      onChange={(code) => onChange(code, names.get(code) || code)}
      options={options}
      placeholder={placeholder}
      searchPlaceholder="Search country or code…"
      searchable
      disabled={disabled}
    />
  );
}
